import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';

const defaultSiteId = () => {
  try {
    const host = window.location.hostname || 'example.com';
    return host.replace(/^www\\./, '');
  } catch {
    return 'example.com';
  }
};

export default function LiveSnippet() {
  const { selectedBrand } = useAuth();
  const apiUrl = 'https://api.glowytics.com/api/live/heartbeat';

  const buildSnippet = useMemo(() => (brandId) => {
    return [
      '<script>',
      '(function(){',
      "  var API='" + apiUrl + "';",
      "  var BRAND_ID='" + brandId + "';",
      "  var SITE_ID=(location.hostname||'default').replace(/^www\\./,'');",
      '  var HEARTBEAT_MS=15000;',
      '',
      '  function getOrSet(k, gen) {',
      '    try {',
      '      var v = localStorage.getItem(k);',
      '      if (!v) { v = gen(); localStorage.setItem(k, v); }',
      '      return v;',
      '    } catch (e) {',
      '      return gen();',
      '    }',
      '  }',
      '  function genId(){',
      "    return String(Date.now()) + String(Math.random()).slice(2);",
      '  }',
      "  var visitorId = getOrSet('glow_v_id', genId);",
      '  var pageId = genId();',
      '',
      '  function send(){',
      '    try {',
      '      var sw = (window.screen && window.screen.width) ? String(window.screen.width) : "0";',
      '      var sh = (window.screen && window.screen.height) ? String(window.screen.height) : "0";',
      '      var payload = {',
      '        brandId: BRAND_ID,',
      '        siteId: SITE_ID,',
      '        url: location.href,',
      '        path: location.pathname,',
      '        ref: document.referrer || null,',
      '        visitorId: visitorId,',
      '        pageId: pageId,',
      '        ts: Date.now(),',
      '        ua: navigator.userAgent,',
      '        lang: navigator.language,',
      '        screen: sw + "x" + sh',
      '      };',
      '      var json = JSON.stringify(payload);',
      '      if (navigator.sendBeacon) {',
      "        var blob = new Blob([json], { type: 'application/json' });",
      '        navigator.sendBeacon(API, blob);',
      '      } else if (window.fetch) {',
      "        fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json });",
      '      } else {',
      '        var xhr = new XMLHttpRequest();',
      "        xhr.open('POST', API, true);",
      "        xhr.setRequestHeader('Content-Type','application/json');",
      '        xhr.send(json);',
      '      }',
      '    } catch (e) {}',
      '  }',
      '',
      '  send();',
      '  var t = setInterval(send, HEARTBEAT_MS);',
      "  document.addEventListener('visibilitychange', function(){",
      '    if (document.hidden) { clearInterval(t); send(); }',
      '    else { send(); t = setInterval(send, HEARTBEAT_MS); }',
      '  });',
      "  window.addEventListener('beforeunload', send);",
      '})();',
      '</script>'
    ].join('\n');
  }, [apiUrl]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Live Traffic Snippet (Copy‑Paste)</h1>
          <p className="text-sm text-gray-500 mt-1">Her marka için hazır snippet. GTM &gt; Custom HTML &gt; All Pages.</p>
        </div>
      </div>

      {!selectedBrand ? (
        <Card className="p-5 rounded-2xl bg-white border">
          <div className="text-sm text-gray-700">
            Lütfen önce bir brand seçin. Sol menüdeki Brand Selector’dan markayı seçtikten sonra bu sayfayı yenileyin.
          </div>
        </Card>
      ) : (
        <Card className="p-5 rounded-2xl bg-white border">
          <div className="mb-3">
            <div className="text-sm text-gray-500">{selectedBrand.id}</div>
            <div className="text-base font-semibold text-gray-900">{selectedBrand.name}</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
            <div className="flex items-end sm:col-span-1">
              <button
                className="w-full sm:w-auto px-3 py-2 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700"
                onClick={()=>copy(buildSnippet(selectedBrand.id))}
              >
                Copy Snippet
              </button>
            </div>
          </div>

          <pre className="bg-gray-50 border rounded-xl p-4 overflow-auto text-xs max-h-72">
{buildSnippet(selectedBrand.id)}
          </pre>
        </Card>
      )}

      <Card className="p-5 rounded-2xl bg-white border">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Setup</h3>
        <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
          <li>Google Tag Manager → Tags → New → Custom HTML.</li>
          <li>Paste the snippet for the selected brand. Trigger: All Pages (DOM Ready or All Events).</li>
          <li>Publish.</li>
        </ol>
      </Card>

      {/* Realtime Traffic Preview */}
      {selectedBrand && (
        <RealtimeTrafficPreview brandId={selectedBrand.id} />
      )}
    </div>
  );
}

function RealtimeTrafficPreview({ brandId }) {
  const [summary, setSummary] = useState(null);
  const [thisPageActive, setThisPageActive] = useState(null);
  const [error, setError] = useState(null);
  const [siteOverride, setSiteOverride] = useState('');
  const [pathOverride, setPathOverride] = useState('/');

  const siteId = (() => {
    try {
      const host = window.location.hostname || 'default';
      return host.replace(/^www\\./, '');
    } catch {
      return 'default';
    }
  })();
  const path = (() => {
    try {
      return window.location.pathname || '/';
    } catch {
      return '/';
    }
  })();
  const selectedSiteId = (siteOverride || '').trim() || siteId;
  const selectedPath = (pathOverride || '').trim() || '/';

  useEffect(() => {
    let abort = false;
    const API = 'https://api.glowytics.com/api/live';

    const fetchData = async () => {
      try {
        const sumUrl = `${API}/summary?brandId=${encodeURIComponent(brandId)}&windowSec=60&limit=10`; // all sites
        const actUrl = `${API}/active?brandId=${encodeURIComponent(brandId)}&siteId=${encodeURIComponent(selectedSiteId)}&path=${encodeURIComponent(selectedPath)}&windowSec=60`;
        const [sumRes, activeRes] = await Promise.all([ fetch(sumUrl), fetch(actUrl) ]);
        if (!sumRes.ok) throw new Error('summary_failed');
        if (!activeRes.ok) throw new Error('active_failed');
        const sumJson = await sumRes.json();
        const actJson = await activeRes.json();
        if (!abort) {
          setSummary(sumJson);
          setThisPageActive(actJson);
          setError(null);
        }
      } catch (e) {
        if (!abort) setError(e?.message || 'error');
      }
    };

    fetchData();
    const t = setInterval(fetchData, 5000);
    return () => {
      abort = true;
      clearInterval(t);
    };
  }, [brandId, selectedSiteId, selectedPath]);

  const totalActive = useMemo(() => {
    if (!summary || !Array.isArray(summary.pages)) return 0;
    return summary.pages.reduce((acc, p) => acc + (Number(p.activeUsers) || 0), 0);
  }, [summary]);

  return (
    <Card className="p-5 rounded-2xl bg-white border">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold text-gray-800">Realtime Traffic (last 60s)</h3>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Site (optional)</label>
            <input
              className="border rounded-lg px-3 py-1.5 text-sm w-56"
              placeholder="example.com"
              value={siteOverride}
              onChange={(e)=>setSiteOverride(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Path (optional)</label>
            <input
              className="border rounded-lg px-3 py-1.5 text-sm w-48"
              placeholder="/"
              value={pathOverride}
              onChange={(e)=>setPathOverride(e.target.value)}
            />
          </div>
        </div>
      </div>
      {error && (
        <div className="text-sm text-red-600 mb-3">Failed to load realtime data.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="rounded-xl border bg-gray-50 p-4">
          <div className="text-xs text-gray-500">Total Active Users</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{totalActive}</div>
        </div>
        <div className="rounded-xl border bg-gray-50 p-4">
          <div className="text-xs text-gray-500">Active on Selected Page</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{Number(thisPageActive?.activeUsers || 0)}</div>
          <div className="text-xs text-gray-500 mt-1 truncate">Site: {selectedSiteId || '-'}</div>
          <div className="text-xs text-gray-500 mt-1 truncate">Path: {selectedPath}</div>
        </div>
        <div className="rounded-xl border bg-gray-50 p-4">
          <div className="text-xs text-gray-500">Window</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">60s</div>
          <div className="text-xs text-gray-500 mt-1">Auto-refresh every 5s</div>
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-800 mb-2">Top Active Pages</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Path</th>
                <th className="py-2 pr-4">Active Users</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.pages || []).map((row) => (
                <tr key={row.path} className="border-b last:border-0">
                  <td className="py-2 pr-4 text-gray-800">{row.path}</td>
                  <td className="py-2 pr-4 text-gray-800">{row.activeUsers}</td>
                </tr>
              ))}
              {(!summary || (summary.pages || []).length === 0) && (
                <tr>
                  <td className="py-3 pr-4 text-gray-500" colSpan={2}>No active traffic in the last 60s.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}


